import { Ban, Scissors } from "lucide-react";

import { cn } from "../../../lib/utils";
import { barToneClass, callSpeechTotals, readCut, type CutCandidate, type CutSummary } from "../lib/speech-cut";

/**
 * Shows what a cut-off turn actually said.
 *
 * The point is to stop a reader treating a cut turn's text as something the
 * caller heard. Three things, in the order that matters when you are watching a
 * call in real time:
 *
 *  1. a bar of how much came out before the cut,
 *  2. the never-voiced tail, struck through, so the full generated text is still
 *     readable without being mistaken for speech,
 *  3. how exact the number is — a word-timestamp measurement and a rate-model
 *     estimate must not look equally trustworthy.
 *
 * Renders nothing for an ordinary turn, so callers can drop it in unconditionally.
 */
export function SpeechCutMeter({
  message,
  className,
  showBasis = true,
}: {
  message: CutCandidate;
  className?: string;
  /** Off for dense surfaces such as a wall tile, where the title is the detail. */
  showBasis?: boolean;
}) {
  const cut = readCut(message);
  if (!cut.visible) return null;

  return (
    <div className={cn("mt-1.5", className)} data-testid="speech-cut">
      <CutHeader cut={cut} />
      <CutBar cut={cut} />
      {cut.unspoken ? <UnspokenTail text={cut.unspoken} /> : null}
      {showBasis ? (
        <p className="mt-1 text-[10px] leading-4 text-ink-3">{cut.basis}</p>
      ) : null}
    </div>
  );
}

function CutHeader({ cut }: { cut: CutSummary }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em]",
          cut.tone === "total" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning",
        )}
        data-testid="speech-cut-label"
      >
        {cut.silent ? <Ban size={9} /> : <Scissors size={9} />}
        {cut.label}
      </span>
      {/* "modelled" is the honest label for the rate-model fallback: the number
          is close but it is an inference, not a measurement. */}
      {cut.measure === "rate" ? (
        <span
          className="rounded bg-soft px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em] text-ink-3"
          title="Estimated from the speech-rate model, not from word timestamps"
        >
          modelled
        </span>
      ) : null}
    </div>
  );
}

function CutBar({ cut }: { cut: CutSummary }) {
  return (
    <div
      className="mt-1 flex h-1.5 w-full overflow-hidden rounded-full bg-soft"
      role="progressbar"
      aria-label="Share of this turn that was spoken"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(cut.ratio * 100)}
      title={cut.basis}
    >
      <span className={cn("block h-full rounded-full", barToneClass(cut.tone))} style={{ width: `${Math.max(2, cut.ratio * 100)}%` }} />
    </div>
  );
}

/**
 * The generated-but-never-voiced tail.
 *
 * Struck through rather than hidden: a supervisor deciding whether the agent was
 * on track needs to see what it was *about* to say as well as what it managed to
 * say, and the difference between the two is the whole finding.
 */
function UnspokenTail({ text }: { text: string }) {
  return (
    <p className="mt-1 break-words text-[11px] leading-4 text-ink-3" data-testid="speech-cut-unspoken">
      <span className="mr-1 font-mono text-[9px] uppercase tracking-[0.12em]">never said</span>
      <span className="line-through decoration-ink-3/60">{text}</span>
    </p>
  );
}

/**
 * The turn's body text — what the caller actually heard.
 *
 * The single place this rule lives. Every live transcript surface needs it, and
 * getting it wrong is the whole bug this feature exists to fix: falling back to
 * `text` when a turn was cut re-presents the generated sentence as speech.
 *
 * So a cut turn renders `spoken_text` even when that is empty — a turn the
 * caller cut before the agent got anything out is legitimately empty, and is
 * shown as such rather than padded back out with the unheard text.
 *
 * `displayed` is the caller's own rendering of the text (the Hindi/English
 * language split, which has already been applied). It is used only for an
 * uncut turn, where nothing here needs to change: passing it in rather than
 * re-deriving it keeps the language transform at the call site that owns it.
 */
export function SpeechCutBody({
  message,
  displayed,
  className,
}: {
  message: CutCandidate;
  /** The text to show when the turn was not cut. */
  displayed: string;
  className?: string;
}) {
  const cut = readCut(message);
  if (!cut.visible) return <p className={className}>{displayed}</p>;
  if (!cut.spoken) {
    return (
      <p className={cn("italic text-ink-3", className)} data-testid="speech-cut-body-empty">
        Nothing was said before the caller interrupted.
      </p>
    );
  }
  return (
    <p className={className} data-testid="speech-cut-body">
      {cut.spoken}
    </p>
  );
}

/**
 * Call-level totals for the drawer header.
 *
 * The per-turn meters answer "where was this one cut". This answers the question
 * that makes a pattern visible mid-call: is the agent getting cut off repeatedly,
 * and how much of what it generates is the caller actually hearing? Rendered
 * nothing at all when the call has no measured cuts, so an ordinary call's header
 * is unchanged.
 */
export function SpeechCutTotals({ messages }: { messages: readonly CutCandidate[] | null | undefined }) {
  const totals = callSpeechTotals(messages);
  if (!totals.visible || !totals.meanRatio) return null;

  const pct = Math.round(totals.meanRatio * 100);
  const turnLabel = totals.cutTurns === 1 ? "turn" : "turns";

  return (
    <span
      className="flex items-center gap-1 font-mono tabular-nums text-warning"
      data-testid="speech-cut-totals"
      title={`${totals.cutTurns} cut ${turnLabel} on this call. On average the caller heard ${pct}% of each, and ${totals.lostChars} characters were generated but never spoken.`}
    >
      <Scissors size={11} />
      {pct}% of agent speech reached the caller
      <span className="text-ink-3">· {totals.cutTurns} cut {turnLabel}</span>
    </span>
  );
}

/**
 * A single-line variant for the wall tile, where three preview turns have to fit
 * without a bar each. Collapses to the number, which is the only part that fits.
 */
export function SpeechCutChip({ message }: { message: CutCandidate }) {
  const cut = readCut(message);
  if (!cut.visible) return null;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 font-mono text-[9px] font-semibold",
        cut.tone === "total" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning",
      )}
      data-testid="speech-cut-chip"
      title={cut.basis}
    >
      {cut.silent ? <Ban size={8} /> : <Scissors size={8} />}
      {cut.silent ? "0%" : `${Math.round(cut.ratio * 100)}%`}
    </span>
  );
}
